import { useMemo } from 'react';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import SpreadsheetNoteParser from 'components/SpreadsheetNotes/SpreadsheetNoteConfig/SpreadsheetNoteParser';
import SpreadsheetNoteReverseParser
, {
  PreviewNoteReverseParser
} from 'components/SpreadsheetNotes/SpreadsheetNoteConfig/SpreadsheetNoteReverseParser';
import {
  UseNoteSpreadsheetProps
} from 'components/SpreadsheetNotes/SpreadsheetNoteConfig/UseNoteSpreadsheet/useNoteSpreadsheet.props';
import { referenceColumnLegend } from 'components/SpreadsheetNotes/types';
import { ReferenceSpreadsheetConfig } from 'components/SpreadsheetNotes/utilities/ReferenceSpreadsheetConfig';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { getTableName } from 'pages/ValuationsAllocation/util/getApproachTableName/getApproachTableName';
import { alphabetGenerator } from 'utilities';

const SPREADSHEET_NOTE_ROWS = 50;
const SPREADSHEET_NOTE_COLUMNS = 10;

const useNoteSpreadsheet = ({
  notes,
  isAttributeMapperEnabled,
  attributeReferenceConfig,
  attributeManager,
  isPreview
}: UseNoteSpreadsheetProps) => {
  const spreadsheets = useMemo(() => {
    const columns = Array.from({ length: SPREADSHEET_NOTE_COLUMNS }, () => ({}));
    const rows = Array.from({ length: SPREADSHEET_NOTE_ROWS }, () => ({}));
    if (notes) {
      return notes.map(note => {
        //   adding an extra column and row help with merged cells
        const rowData = Object.values(note.content.row_data ?? {});
        const sheetRows = [...rowData, {}];
        const columnData = Object.values(note.content.column_data ?? {});
        const columnDataLength = columnData.length;
        const sheetColumns = [...columnData, {}];
        columnData.push({});
        const rowConfig = rowData.length ? sheetRows : rows;
        const columnConfig = columnDataLength ? sheetColumns : columns;
        const alphabet = alphabetGenerator([], columnConfig.length);
        if (isAttributeMapperEnabled) {
          alphabet.unshift(referenceColumnLegend);
          rowConfig.unshift({});
          columnConfig.unshift({});
        }

        let ConfigClass;
        let reverseParseFn = SpreadsheetNoteReverseParser;

        if (isAttributeMapperEnabled && attributeReferenceConfig) {
          ConfigClass = ReferenceSpreadsheetConfig;
        } else {
          ConfigClass = SpreadsheetConfig;
        }
        if (isPreview) {
          reverseParseFn = PreviewNoteReverseParser;
        }

        const name = note.name ? getTableName(note.name) : `Spreadsheet_Note${note.id || 0}`;
        return new ConfigClass({
          tableData: { attributeReferenceConfig, isAttributeMapperEnabled, sheetName: note.name },
          reverseParser: reverseParseFn,
          parser: params => SpreadsheetNoteParser(params, isPreview, attributeManager),
          rowConfig,
          reverseParseAfterLoad: true,
          columns: columnConfig,
          initialObj: note,
          alphabet,
          name,
          showTitlesColumn: false,
          hasColTitle: false,
          ignoreCellExpressionErrors: true,
        });
      });
    }
  }, [notes, isAttributeMapperEnabled, attributeReferenceConfig, attributeManager, isPreview]);

  return { spreadsheets };
};

export default useNoteSpreadsheet;